import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useSnackbar } from '@/features/snackbar';

import type { NarrativeFragmentType } from '../narrativeFragment';
import type { CreatePublicNarrative } from './types';

import { mutationFn } from './mutationFn';
import { getQueryKey } from '../queryKeys';
import { useNarrativeInvalidate } from '../useNarrativeInvalidate';

interface Props {
  onSuccess: (narrative: NarrativeFragmentType) => void;
}

export function useCreatePublicNarrative({ onSuccess }: Props) {
  const queryClient = useQueryClient();
  const { addSnackbar } = useSnackbar();
  const { invalidate } = useNarrativeInvalidate();

  const mutation = useMutation({
    mutationFn,
    onSuccess: async (narrative) => {
      await invalidate({ companyId: narrative.company.id });

      queryClient.setQueryData<CreatePublicNarrative>(
        getQueryKey(narrative.id),
        () => narrative
      );
      onSuccess(narrative);
      addSnackbar({
        type: 'positive',
        lifeTime: 1000,
        message: 'Successfully created narrative',
      });
    },
    onError: () => {
      addSnackbar({
        type: 'negative',
        lifeTime: 2000,
        message: 'Failed to create narrative',
      });
    },
  });

  return mutation;
}
